class CdxSearch extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector('.cdx-search-modal');
    this.drawer = this.modal.querySelector('.cdx-search-drawer');
    this.overlay = this.modal.querySelector('.cdx-search-overlay');
    this.trigger = this.querySelector('.cdx-search-trigger');
    this.closeBtn = this.modal.querySelector('.cdx-search-close');
    this.input = this.modal.querySelector('.cdx-search-input');
    this.resetBtn = this.modal.querySelector('.cdx-search-reset');
    this.resultsContainer = this.modal.querySelector('[data-cdx-search-results]');
    this.statusEl = this.modal.querySelector('[data-cdx-search-status]');
    document.body.appendChild(this.modal);
    this.loadingText = this.getAttribute('data-loading-text') || 'Loading...';
    this.cachedResults = {};
    this.searchTerm = '';
    this.isOpen = false;
    this.abortController = new AbortController();

    this.trigger.addEventListener('click', () => this.open());
    this.closeBtn.addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', () => this.close());
    this.resetBtn.addEventListener('click', () => this.clearResults());

    this.resultsContainer.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cdx-tab]');
      if (!btn) return;
      const tab = btn.dataset.cdxTab;
      this.resultsContainer.querySelectorAll('[data-cdx-tab]').forEach(b => {
        const active = b.dataset.cdxTab === tab;
        b.classList.toggle('text-[#76232F]', active);
        b.classList.toggle('border-[#76232F]', active);
        b.classList.toggle('text-[#76232F]/40', !active);
        b.classList.toggle('border-transparent', !active);
      });
      this.resultsContainer.querySelectorAll('[data-cdx-tab-content]').forEach(p => {
        p.classList.toggle('hidden', p.dataset.cdxTabContent !== tab);
      });
    });

    this.input.addEventListener('input', debounce(() => this.onChange(), 300));
    this.modal.addEventListener('keyup', (e) => this.onKeyup(e));
    this.modal.addEventListener('keydown', (e) => {
      if (e.code === 'ArrowUp' || e.code === 'ArrowDown') e.preventDefault();
    });

    this.modal.querySelector('form').addEventListener('submit', (e) => {
      const selectedLink = this.modal.querySelector('[aria-selected="true"] a');
      if (selectedLink) {
        e.preventDefault();
        selectedLink.click();
      } else if (!this.input.value.trim()) {
        e.preventDefault();
      }
    });
  }

  onChange() {
    const newTerm = this.input.value.trim();
    this.searchTerm = newTerm;
    this.resetBtn.classList.toggle('hidden', !newTerm.length);

    if (!newTerm.length) {
      this.clearResults();
      return;
    }

    this.getSearchResults(newTerm);
  }

  onKeyup(e) {
    switch (e.code) {
      case 'Escape': this.close(); break;
      case 'ArrowUp': this.switchOption('up'); break;
      case 'ArrowDown': this.switchOption('down'); break;
      case 'Enter': this.selectOption(); break;
    }
  }

  switchOption(direction) {
    const moveUp = direction === 'up';
    const items = Array.from(this.modal.querySelectorAll('[role="option"]')).filter(
      (el) => el.offsetParent !== null
    );
    if (!items.length) return;

    const selected = this.modal.querySelector('[aria-selected="true"]');
    if (moveUp && !selected) return;

    const selectedIdx = items.indexOf(selected);
    let newIdx;
    if (!moveUp && selected) {
      newIdx = selectedIdx === items.length - 1 ? 0 : selectedIdx + 1;
    } else if (!moveUp) {
      newIdx = 0;
    } else {
      newIdx = selectedIdx === 0 ? items.length - 1 : selectedIdx - 1;
    }

    if (selected) selected.setAttribute('aria-selected', 'false');
    items[newIdx].setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', items[newIdx].id);
  }

  selectOption() {
    const selected = this.modal.querySelector('[aria-selected="true"] a');
    if (selected) selected.click();
  }

  getSearchResults(term) {
    const key = term.replace(/\s+/g, '-').toLowerCase();

    this.abortController.abort();
    this.abortController = new AbortController();

    this.modal.classList.add('is-loading');
    this.announce(this.loadingText);

    if (this.cachedResults[key]) {
      this.renderResults(this.cachedResults[key]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(term)}&resources[type]=product,collection,page&resources[limit]=5&resources[options][fields]=title,tag,variants.title,product_type&section_id=cdx-predictive-search`,
      { signal: this.abortController.signal }
    )
      .then((res) => {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then((text) => {
        const html = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-cdx-predictive-search').innerHTML;
        this.cachedResults[key] = html;
        this.renderResults(html);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        this.modal.classList.remove('is-loading');
        throw err;
      });
  }

  renderResults(html) {
    this.resultsContainer.innerHTML = html;
    this.modal.classList.remove('is-loading');
    this.setAttribute('results', 'true');

    const count = this.modal.querySelector('[data-cdx-search-count]')?.textContent?.trim();
    if (count !== undefined) {
      this.announce(`${count} Ergebnis${count === '1' ? '' : 'se'}`);
    }
  }

  clearResults() {
    this.resultsContainer.innerHTML = '';
    this.removeAttribute('results');
  }

  announce(text) {
    this.statusEl.setAttribute('aria-hidden', 'false');
    this.statusEl.textContent = text;
    setTimeout(() => this.statusEl.setAttribute('aria-hidden', 'true'), 1000);
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;

    this.modal.removeAttribute('hidden');
    requestAnimationFrame(() => {
      this.drawer.classList.add('is-open');
      this.overlay.classList.add('is-open');
    });

    this.trigger.setAttribute('aria-expanded', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    const header = document.querySelector('cdx-header');
    if (header) header.classList.add('is-search-open');

    setTimeout(() => this.input.focus(), 50);
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';

    const header = document.querySelector('cdx-header');
    if (header) header.classList.remove('is-search-open');

    this.drawer.addEventListener(
      'transitionend',
      () => { if (!this.isOpen) this.modal.setAttribute('hidden', ''); },
      { once: true }
    );
  }
}

if (!customElements.get('cdx-search')) {
  customElements.define('cdx-search', CdxSearch);
}
